// The designed empty / loading / error states shared by the desktop modal and the mobile door screen
// (never a blank surface). The 403 branch is the client mirror of the server's Content-role
// forbidden (demo step 5): if a Content seat reaches the route directly, the API 403s and this renders the
// "not available for your role" state rather than a raw error.
import { Button, EmptyState, Spinner } from "@cntrl/ui";
import { ApiError } from "../../app/hooks/apiError.js";
import styles from "./GuestListStates.module.scss";

/** Centered spinner while the list loads. */
export function GuestLoading({ label = "Loading guest list…" }: { label?: string }) {
  return (
    <div className={styles.center} role="status" aria-live="polite">
      <Spinner size={22} label={label} />
      <p className={styles.centerText}>{label}</p>
    </div>
  );
}

/** Content-role / insufficient-capability state (server 403). File-local — reached via {@link GuestQueryError}. */
function GuestForbidden() {
  return (
    <EmptyState
      title="Guest list unavailable"
      description="Your role on this band doesn't include the guest list."
    />
  );
}

/** Generic load error with a retry, showing the server message verbatim. File-local.*/
function GuestError({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <EmptyState
      title="Couldn't load the guest list"
      description={message}
      action={<Button onClick={onRetry}>Retry</Button>}
    />
  );
}

/**
 * Renders the right state for a failed guest query: 403 → the forbidden state (the Content-role mirror of
 * the server 403); anything else → the generic error with the server detail verbatim + a retry.
 */
export function GuestQueryError({ error, onRetry }: { error: unknown; onRetry: () => void }) {
  const status = error instanceof ApiError ? error.status : 0;
  if (status === 403) return <GuestForbidden />;
  const message = error instanceof Error && error.message ? error.message : "Please try again.";
  return <GuestError message={message} onRetry={onRetry} />;
}
